import { useConfirmationModal, useSnackbars } from '@customerio/pluma-components/react';
import { useServicesQuery, useServicesMutation } from '@/services/api';
import { useQueryClient } from '@tanstack/react-query';
import { useAnalytics } from '@/services/analytics';
import { useWorkspaceIdFromCurrentUrl } from '@/utils/workspace-id';
import { useAuth } from '@/services/auth';
import { PlanFeature } from '@/services/auth/types';

const DEFAULT_MAX_ACTIVE = 1;

export function useRoutines() {
  const workspaceId = useWorkspaceIdFromCurrentUrl() ?? 0;
  const { account } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirmationModal();
  const snackbars = useSnackbars();
  const { track } = useAnalytics();
  const mutation = useServicesMutation('updateCopilotCron');

  // Read the active-routine limit from gated features (set per plan in the backend).
  // The backend's GetGatedFeatures merges defaults + plan + account overrides,
  // so the frontend just trusts the value it receives.
  // Per backend convention: limit === false or limit === 0 means unlimited.
  //
  // NOTE: This intentionally does NOT use hasUnlimitedPlanFeatureAccess or check
  // account.premium. Unlike binary feature gates (audit logs, etc.), routines need
  // a numeric limit (1 vs 5), not just unlimited/limited. The backend's
  // defaultFeatureLimits() is plan-aware and returns the correct limit per plan
  // tier, so the frontend doesn't need to duplicate that logic.
  const routinesActiveFeature = account.planFeatures[PlanFeature.RoutinesActive];
  const isUnlimited = routinesActiveFeature?.limit === false || routinesActiveFeature?.limit === 0;
  const maxActive: number = isUnlimited
    ? Infinity
    : routinesActiveFeature?.enabled === true && typeof routinesActiveFeature.limit === 'number'
    ? routinesActiveFeature.limit
    : DEFAULT_MAX_ACTIVE;

  const routinesQuery = useServicesQuery(
    'listCopilotCrons',
    { params: { environment_id: workspaceId } },
    { enabled: workspaceId > 0 },
  );

  const routines = routinesQuery.data?.routines ?? [];

  const invalidateRoutines = () => queryClient.invalidateQueries({ queryKey: ['listCopilotCrons'] });

  const toggleRoutine = async (routineId: number) => {
    const routine = routines.find((r) => r.id === routineId);
    if (routine == null) return;

    if (!routine.paused) {
      // Pause
      try {
        await mutation.mutateAsync({
          params: { environment_id: workspaceId, cron_id: routineId },
          body: { paused: true },
        });
        await invalidateRoutines();
        track('aiAssistant', 'routinePaused', {});
        snackbars.create({ variant: 'success', message: `"${routine.name}" has been paused.` });
      } catch {
        await invalidateRoutines();
        snackbars.create({ variant: 'error', message: 'Failed to pause routine. Please try again.' });
      }
      return;
    }

    // Resume — check if at the active limit
    const activeRoutines = routines.filter((r) => !r.paused);
    const atLimit = activeRoutines.length >= maxActive;

    if (atLimit && maxActive === 1) {
      // Single-active plan: offer to swap via confirmation modal
      track('aiAssistant', 'limitReached', {
        Feature: 'routines',
        Limit: maxActive,
        'Current Count': activeRoutines.length,
      });
      const activeRoutine = activeRoutines[0];
      if (activeRoutine == null) return;
      await confirm({
        title: `Resume ${routine.name}?`,
        message: `This will pause "${activeRoutine.name}". One routine can be active at a time.`,
        onConfirm: async () => {
          try {
            await mutation.mutateAsync({
              params: { environment_id: workspaceId, cron_id: activeRoutine.id },
              body: { paused: true },
            });
            track('aiAssistant', 'routinePaused', {});
          } catch {
            await invalidateRoutines();
            snackbars.create({ variant: 'error', message: 'Failed to pause the active routine. Please try again.' });
            return;
          }

          try {
            await mutation.mutateAsync({
              params: { environment_id: workspaceId, cron_id: routineId },
              body: { paused: false },
            });
            await invalidateRoutines();
            track('aiAssistant', 'routineResumed', {});
            snackbars.create({ variant: 'success', message: `"${routine.name}" is now active.` });
          } catch {
            await invalidateRoutines();
            snackbars.create({
              variant: 'error',
              message: `Failed to resume "${routine.name}". "${activeRoutine.name}" was paused — you may need to re-enable it manually.`,
            });
          }
        },
      });
      return;
    }

    if (atLimit) {
      // Multi-active plan at limit: tell the user to free a slot
      track('aiAssistant', 'limitReached', {
        Feature: 'routines',
        Limit: maxActive,
        'Current Count': activeRoutines.length,
      });
      snackbars.create({
        variant: 'error',
        message: `You have ${String(activeRoutines.length)} active routines. Pause one before enabling another.`,
      });
      return;
    }

    // Under the limit — just resume
    try {
      await mutation.mutateAsync({
        params: { environment_id: workspaceId, cron_id: routineId },
        body: { paused: false },
      });
      await invalidateRoutines();
      track('aiAssistant', 'routineResumed', {});
      snackbars.create({ variant: 'success', message: `"${routine.name}" is now active.` });
    } catch {
      await invalidateRoutines();
      snackbars.create({ variant: 'error', message: 'Failed to resume routine. Please try again.' });
    }
  };

  return {
    routines,
    isLoading: routinesQuery.isLoading,
    isError: routinesQuery.isError,
    toggleRoutine,
    workspaceId,
    maxActive,
  };
}
